import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useProject } from "./useProject";

export interface JotFormFormMapping {
  id: number;
  project_id: number;
  jotform_form_id: string;
  form_launch_id: number | null;
  form_title: string | null;
  is_referral_form: boolean;
  parent_form_id: number | null;
  validation_threshold: number;
  created_at: string;
  updated_at: string;
  parent?: {
    id: number;
    form_title: string | null;
    jotform_form_id: string;
  };
}

export interface JotFormFormFromAPI {
  id: string;
  title: string;
  status: string;
  created_at: string;
  updated_at: string;
  count: number;
}

export function useJotFormForms() {
  const { projectId } = useProject();
  const queryClient = useQueryClient();

  // Query to get configured forms from database
  const configuredFormsQuery = useQuery({
    queryKey: ['jotform-configured-forms', projectId],
    queryFn: async () => {
      if (!projectId) {
        throw new Error('Project ID is required');
      }

      const { data, error } = await supabase
        .from('jotform_form_mappings')
        .select(`
          *,
          parent:parent_form_id (
            id,
            form_title,
            jotform_form_id
          )
        `)
        .eq('project_id', projectId)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching configured forms:', error);
        throw error;
      }

      return data as JotFormFormMapping[];
    },
    enabled: !!projectId,
  });

  // Query to get forms from JotForm API
  const jotformFormsQuery = useQuery({
    queryKey: ['jotform-forms-api'],
    queryFn: async () => {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

      const response = await fetch(`${supabaseUrl}/functions/v1/jotform-api`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${supabaseAnonKey}`
        },
        body: JSON.stringify({
          action: 'list-forms',
          params: {
            limit: 1000,
            offset: 0,
            orderby: 'created_at'
          }
        })
      });

      if (!response.ok) {
        throw new Error('Error fetching forms from JotForm');
      }

      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.error || 'Error fetching forms from JotForm');
      }
      
      return result.data.forms as JotFormFormFromAPI[];
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  // Mutation to create or update form mapping
  const saveFormMutation = useMutation({
    mutationFn: async (formData: {
      id?: number;
      jotform_form_id: string;
      form_title: string;
      form_launch_id?: number | null;
      is_referral_form: boolean;
      parent_form_id?: number | null;
      validation_threshold: number;
    }) => {
      if (!projectId) {
        throw new Error('Project ID is required');
      }

      const dataToSave = {
        project_id: projectId,
        jotform_form_id: formData.jotform_form_id,
        form_title: formData.form_title,
        form_launch_id: formData.form_launch_id,
        is_referral_form: formData.is_referral_form,
        parent_form_id: formData.parent_form_id,
        validation_threshold: formData.validation_threshold,
      };

      if (formData.id) {
        // Update existing
        const { data, error } = await supabase
          .from('jotform_form_mappings')
          .update(dataToSave)
          .eq('id', formData.id)
          .select()
          .single();

        if (error) throw error;
        return data;
      } else {
        // Insert new
        const { data, error } = await supabase
          .from('jotform_form_mappings')
          .insert(dataToSave)
          .select()
          .single();

        if (error) throw error;
        return data;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['jotform-configured-forms', projectId] });
    },
  });

  // Mutation to delete form mapping
  const deleteFormMutation = useMutation({
    mutationFn: async (formId: number) => {
      const { error } = await supabase
        .from('jotform_form_mappings')
        .delete()
        .eq('id', formId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['jotform-configured-forms', projectId] });
    },
  });

  return {
    configuredForms: configuredFormsQuery.data || [],
    isLoadingConfigured: configuredFormsQuery.isLoading,
    jotformForms: jotformFormsQuery.data || [],
    isLoadingJotform: jotformFormsQuery.isLoading,
    saveForm: saveFormMutation.mutateAsync,
    isSaving: saveFormMutation.isPending,
    deleteForm: deleteFormMutation.mutateAsync,
    isDeleting: deleteFormMutation.isPending,
    refetch: () => {
      configuredFormsQuery.refetch();
      jotformFormsQuery.refetch();
    },
  };
}

export function useJotFormForm(formId: number | null) {
  const { projectId } = useProject();

  return useQuery({
    queryKey: ['jotform-form-mapping', formId, projectId],
    queryFn: async () => {
      if (!formId || !projectId) {
        return null;
      }

      const { data, error } = await supabase
        .from('jotform_form_mappings')
        .select(`
          *,
          parent:parent_form_id (
            id,
            form_title,
            jotform_form_id
          )
        `)
        .eq('id', formId)
        .eq('project_id', projectId)
        .maybeSingle();

      if (error) {
        console.error('Error fetching form mapping:', error);
        throw error;
      }

      return data as JotFormFormMapping | null;
    },
    enabled: !!formId && !!projectId,
  });
}
